const SCOPES = 'user-read-currently-playing user-read-playback-state user-read-private'
const AUTH_URL  = 'https://accounts.spotify.com/authorize'
const TOKEN_URL = 'https://accounts.spotify.com/api/token'
const API_BASE  = 'https://api.spotify.com/v1'
export const DEFAULT_CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID || ''
export const CONFIGURED_REDIRECT_URI = process.env.REACT_APP_SPOTIFY_REDIRECT_URI || ''
const TOKEN_STORAGE_KEY = 'sp_token'
const USER_STORAGE_KEY = 'sp_user'

function generateVerifier(len = 128) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  return Array.from(crypto.getRandomValues(new Uint8Array(len)))
    .map(x => chars[x % chars.length]).join('')
}

async function sha256base64url(plain) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain))
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export function getRedirectUri() {
  if (CONFIGURED_REDIRECT_URI) return CONFIGURED_REDIRECT_URI.replace(/\/+$/, '')
  const { origin, pathname } = window.location
  return origin + pathname.replace(/\/+$/, '')
}

export async function initiateAuth(clientId, returnPath = window.location.pathname) {
  const verifier = generateVerifier()
  const challenge = await sha256base64url(verifier)
  const state = generateVerifier(32)
  sessionStorage.setItem('sp_verifier', verifier)
  sessionStorage.setItem('sp_client_id', clientId)
  sessionStorage.setItem('sp_auth_state', state)
  sessionStorage.setItem('sp_return_path', returnPath)
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    scope: SCOPES,
    state,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  })
  window.location.href = `${AUTH_URL}?${params}`
}

function storeToken(data) {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
    ...data,
    issued_at: Date.now(),
    expires_at: Date.now() + data.expires_in * 1000,
  }))
}

export function loadStoredToken() {
  try { return JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || 'null') } catch { return null }
}

export function clearStoredToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY)
  localStorage.removeItem(USER_STORAGE_KEY)
}

export function loadStoredUser() {
  try { return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || 'null') } catch { return null }
}

export function storeUser(profile) {
  if (!profile) return
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({
    display_name: profile.display_name || profile.id || '',
    id: profile.id || '',
    stored_at: Date.now(),
  }))
}

export async function exchangeCode(code, clientId, state) {
  const verifier = sessionStorage.getItem('sp_verifier')
  const expectedState = sessionStorage.getItem('sp_auth_state')
  if (!verifier) throw new Error('No code verifier found — please try connecting again.')
  if (!state || state !== expectedState) throw new Error('Spotify login state did not match — please try again.')
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: verifier,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error_description || `Token exchange failed (${res.status})`)
  }
  const data = await res.json()
  storeToken(data)
  sessionStorage.removeItem('sp_verifier')
  sessionStorage.removeItem('sp_auth_state')
  localStorage.setItem('sp_client_id', clientId)
  return data.access_token
}

export async function refreshAccessToken(clientId) {
  const stored = loadStoredToken()
  if (!stored?.refresh_token) return null
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'refresh_token',
      refresh_token: stored.refresh_token,
    }),
  })
  if (!res.ok) { clearStoredToken(); return null }
  const data = await res.json()
  storeToken({ ...data, refresh_token: data.refresh_token || stored.refresh_token })
  return data.access_token
}

export async function getValidToken(clientId) {
  const stored = loadStoredToken()
  if (!stored) return null
  if (Date.now() < stored.expires_at - 60_000) return stored.access_token
  return refreshAccessToken(clientId)
}

async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 204 || res.status === 404) return null
  if (res.status === 401) {
    clearStoredToken()
    return null
  }
  if (!res.ok) return null
  return res.json()
}

export const getMe = (token) => apiGet('/me', token)
export const getPlaybackState = (token) => apiGet('/me/player', token)
export const getCurrentlyPlaying = (token) => apiGet('/me/player/currently-playing', token)
export const getAudioAnalysis = (id, token) => apiGet(`/audio-analysis/${id}`, token)
export const getAudioFeatures = (id, token) => apiGet(`/audio-features/${id}`, token)
export const getQueue = (token) => apiGet('/me/player/queue', token)
